import React, { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { entityKycStatusAction } from "../../actions/entityKycStatusAction";
import {
  useUploadDetails,
  ENTITY_INFO_FIELDS_BEFORE_GST,
  ENTITY_INFO_FIELDS_AFTER_GST,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  DOCS_BANK,
  renderFields,
  EntityDocumentsSection,
} from "./UploadDetailsData";
import GSTEntries from "./Components/GSTEntries";
import UtilityForm from "./Components/UtilityForm";
import MSMESection from "./Components/MSMESection";
import ThirdPartySection from "./Components/ThirdPartySection";
import DocumentUpload from "../../components/DocumentUpload";
import { canUploadDoc } from "../../utils/deficiencyUtils";
import { useDocumentViewer } from "../../components/DocumentViewerModal/useDocumentViewer";
import companyLogo from "../../assets/images/AcrossLogo.png";
import "./index.scss";

const UploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const entityPrimaryId = parseInt(
    searchParams.get("onboardingId") ||
      searchParams.get("entityPrimaryId") ||
      "0",
    10,
  );
  const { viewDocument, DocumentViewer } = useDocumentViewer(entityPrimaryId);

  const handleViewDocument = (filePath, displayName = "Document") => {
    if (filePath) {
      viewDocument(filePath, { displayName });
    }
  };

  // Check if KYC already submitted — redirect to thank-you if so
  useEffect(() => {
    const id =
      searchParams.get("onboardingId") || searchParams.get("entityPrimaryId");
    if (!id) return;
    entityKycStatusAction(parseInt(id, 10)).then(({ isDocumentUploaded }) => {
      if (isDocumentUploaded) {
        const entityName = searchParams.get("EntityName") || "";
        const entityId =
          searchParams.get("entityId") || searchParams.get("EntityId") || "";
        navigate(
          `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}&alreadySubmitted=true`,
          { replace: true },
        );
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    formData,
    errors,
    setErrors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleSaveDraft,
    handleDocumentUpload,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    entityId,
    showThirdParty,
    // beneficiaryMatchStatus, entityName — not used in JSX currently
    existingDocuments,
    existingPaths,
    deficiencyMap,
    isDeficiencyMode,
    entityCategory,
    uploadingDocKeys,
    isDocUploading,
    isUploadingDoc,
    uploadingDocCount,
    hasPendingFileWithoutPath,
    hasFailedUploads,
  } = useUploadDetails();

  // In deficiency mode: all fields readonly except deficient ones
  const formReadOnly = isDeficiencyMode;

  // Helper: get existing uploaded file for a doc key
  const getExistingFile = (docKey) => {
    const doc = existingDocuments.find((d) => d.key === docKey);
    const uploaded = doc?.files?.find((f) => f.isUploaded) || null;
    if (uploaded) return uploaded;
    const fallbackPath =
      existingPaths?.[docKey] ||
      existingPaths?.[docKey?.replace("doc_", "")] ||
      (docKey === "aadharCardBackPath" || docKey === "AadharCardBackPath"
        ? existingPaths?.AadharCardBackPath || existingPaths?.aadharCardBackPath
        : "");
    if (fallbackPath) {
      return {
        isUploaded: true,
        fileName: fallbackPath.split(/[\\/]/).pop() || "Document",
        filePath: fallbackPath,
      };
    }
    return null;
  };

  // Can a doc be re-uploaded?
  const docUploadable = (docKey) =>
    canUploadDoc(docKey, deficiencyMap, isDeficiencyMode);

  return (
    <div className="upload-details-page">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__main">
          <div className="page-header__brand">
            <img
              src={companyLogo}
              alt="Company logo"
              className="page-header__logo"
            />
          </div>
          <div className="page-header__text">
            <h1 className="page-title">Complete KYC OnBoarding Details</h1>
            <p className="page-subtitle">
              Fill in the remaining details to complete onboarding registration
            </p>
          </div>
        </div>
        <div className="page-header__badges">
          {entityId && (
            <div className="header-chip header-chip--id">
              <span className="header-chip__label">Entity ID</span>
              <span className="header-chip__value">#{entityId}</span>
            </div>
          )}
          {formData.entityName && (
            <div className="header-chip header-chip--entity">
              <span className="header-chip__label">Entity</span>
              <span className="header-chip__value">{formData.entityName}</span>
            </div>
          )}
          {formData.entityType && (
            <div className="header-chip header-chip--type">
              <span className="header-chip__label">Type</span>
              <span className="header-chip__value">
                {formData.entityType.replace(/_/g, " ")}
              </span>
            </div>
          )}
          {entityCategory && (
            <div className="header-chip header-chip--category">
              <span className="header-chip__label">Category</span>
              <span className="header-chip__value">{entityCategory}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Single form card ── */}
      <form onSubmit={handleSubmit} className="details-form">
        <div className="form-card">
          {/* Deficiency mode banner */}
          {isDeficiencyMode && (
            <div className="deficiency-banner">
              <span className="deficiency-banner__icon">⚠️</span>
              <div>
                <strong>Deficiency Notice</strong>
                <p>
                  Some documents/fields have been marked as deficient. Only the
                  highlighted fields (in red) can be updated. All other fields
                  are read-only.
                </p>
              </div>
            </div>
          )}

          {/* Entity Information */}
          <section className="form-section">
            <div className="section-heading">
              {/* <span className="section-heading__badge">01</span> */}
              <div>
                <h2 className="section-title">KYC Information</h2>
                <p className="section-desc">
                  Entity details and official business identification
                </p>
              </div>
            </div>

            <div className="form-grid">
              {renderFields(
                ENTITY_INFO_FIELDS_BEFORE_GST,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>

            {/* GST Entries Section — directly after Nature of Business & before E-Invoice */}
            <div
              className="gst-section-wrapper"
              style={{ marginTop: "1.5rem" }}
            >
              <div className="section-heading" style={{ marginBottom: "1rem" }}>
                <div>
                  <h3 className="section-title" style={{ fontSize: "1.05rem" }}>
                    GST Information
                  </h3>
                  <p className="section-desc">
                    Goods and Services Tax registration and certificates
                  </p>
                </div>
              </div>
              <div className="form-grid">
                {renderFields(
                  GST_FIELDS,
                  formData,
                  errors,
                  formReadOnly ? undefined : handleChange,
                  formReadOnly ? undefined : handleBlur,
                  formReadOnly,
                )}
              </div>
              <GSTEntries
                isGSTRegistered={formData.isGSTRegistered}
                gstEntries={gstEntries}
                addGstEntry={formReadOnly ? undefined : addGstEntry}
                removeGstEntry={formReadOnly ? undefined : removeGstEntry}
                handleGstChange={formReadOnly ? undefined : handleGstChange}
                handleGstBlur={formReadOnly ? undefined : handleGstBlur}
                gstFiles={gstFiles}
                handleGstFileChange={(index, file) => {
                  const key = `gst_file_${index}`;
                  if (!docUploadable(key)) return;
                  handleGstFileChange(index, file);
                  handleDocumentUpload(key, file);
                  if (file)
                    setErrors((prev) => {
                      const e = { ...prev };
                      delete e[`gst_certificate_${index}`];
                      return e;
                    });
                }}
                gstCertErrors={errors}
                gstErrors={gstErrors}
                readOnly={formReadOnly}
                deficiencyMap={deficiencyMap}
                isDeficiencyMode={isDeficiencyMode}
                onViewDocument={handleViewDocument}
                isDocUploading={isDocUploading}
                uploadingDocKeys={uploadingDocKeys}
                existingGstFiles={existingDocuments
                  .filter((d) => d.key.startsWith("gst_file_"))
                  .sort((a, b) => {
                    const ai = parseInt(a.key.replace("gst_file_", ""), 10);
                    const bi = parseInt(b.key.replace("gst_file_", ""), 10);
                    return ai - bi;
                  })
                  .map((d) => d.files?.find((f) => f.isUploaded) || null)}
              />
              <UtilityForm
                isGSTRegistered={formData.isGSTRegistered}
                formData={formData}
                errors={errors}
                handleChange={formReadOnly ? undefined : handleChange}
                handleBlur={formReadOnly ? undefined : handleBlur}
                utilityFile={utilityFile}
                handleUtilityFileChange={(file) => {
                  if (!docUploadable("doc_utility_bill")) return;
                  setUtilityFile(file);
                  handleDocumentUpload("utility_file", file);
                }}
                existingUtilityFile={getExistingFile("doc_utility_bill")}
                onViewExisting={() => {
                  const f = getExistingFile("doc_utility_bill");
                  if (f?.filePath)
                    handleViewDocument(f.filePath, "Utility Bill Document");
                }}
                readOnly={formReadOnly}
                isDeficient={!!deficiencyMap["doc_utility_bill"]}
                isUploading={
                  isDocUploading
                    ? isDocUploading("utility_file") ||
                      isDocUploading("doc_utility_bill")
                    : false
                }
              />
            </div>

            {/* E-Invoice & Entity Type Fields — directly after GST */}
            <div className="form-grid" style={{ marginTop: "1.5rem" }}>
              {renderFields(
                ENTITY_INFO_FIELDS_AFTER_GST,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>

            <EntityDocumentsSection
              entityType={formData.entityType}
              isGSTRegistered={formData.isGSTRegistered}
              entityFiles={entityFiles}
              onEntityFileChange={(key, file) => {
                if (!docUploadable(key)) return;
                handleEntityFileChange(key, file);
                handleDocumentUpload(key, file);
                if (file) {
                  setErrors((prev) => {
                    const e = { ...prev };
                    delete e[`entity_doc_${key}`];
                    return e;
                  });
                }
              }}
              entityDocInputs={entityDocInputs}
              onEntityDocInputChange={handleEntityDocInputChange}
              onEntityDocInputBlur={handleEntityDocInputBlur}
              errors={errors}
              existingDocuments={existingDocuments}
              existingPaths={existingPaths}
              onViewDocument={handleViewDocument}
              deficiencyMap={deficiencyMap}
              isDeficiencyMode={isDeficiencyMode}
              isDocUploading={isDocUploading}
              uploadingDocKeys={uploadingDocKeys}
            />
          </section>

          <div className="section-divider" />

          {/* Contact Person */}
          <section className="form-section">
            <div className="section-heading">
              {/* <span className="section-heading__badge">02</span> */}
              <div>
                <h2 className="section-title">Contact Person Details</h2>
                <p className="section-desc">
                  Designated authorized representative contacts
                </p>
              </div>
            </div>
            <div className="form-grid">
              {renderFields(
                CONTACT_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
          </section>

          <div className="section-divider" />

          {/* MSME Information */}
          <section className="form-section">
            <div className="section-heading">
              {/* <span className="section-heading__badge">03</span> */}
              <div>
                <h2 className="section-title">MSME Information</h2>
                {/* <p className="section-desc">
                  Micro, Small and Medium Enterprises enterprise credentials
                </p> */}
              </div>
            </div>
            <div className="form-grid">
              {renderFields(
                MSME_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
            <MSMESection
              isMSMERegistered={formData.isMSMERegistered}
              formData={formData}
              errors={errors}
              handleChange={formReadOnly ? undefined : handleChange}
              handleBlur={formReadOnly ? undefined : handleBlur}
              msmeFile={msmeFile}
              handleMsmeFileChange={(file) => {
                if (!docUploadable("doc_msme_certificate")) return;
                setMsmeFile(file);
                handleDocumentUpload("msme_file", file);
              }}
              existingMsmeFile={getExistingFile("doc_msme_certificate")}
              onViewExisting={() => {
                const f = getExistingFile("doc_msme_certificate");
                if (f?.filePath)
                  handleViewDocument(f.filePath, "MSME Certificate");
              }}
              readOnly={formReadOnly}
              isDeficient={!!deficiencyMap["doc_msme_certificate"]}
              isUploading={
                isDocUploading
                  ? isDocUploading("msme_file") ||
                    isDocUploading("doc_msme_certificate")
                  : false
              }
            />
          </section>

          <div className="section-divider" />

          {/* Bank Details */}
          <section className="form-section">
            <div className="section-heading">
              {/* <span className="section-heading__badge">04</span> */}
              <div>
                <h2 className="section-title">Bank Details</h2>
                <p className="section-desc">
                  Banking settlement credentials and supporting documentation
                </p>
              </div>
            </div>
            <div className="form-grid-radio">
              <label className="radio-label">Provide Bank Details?</label>
              <div className="radio-group">
                {["yes", "no"].map((v) => (
                  <label key={v} className="radio-option">
                    <input
                      type="radio"
                      name="hasBankDetails"
                      value={v}
                      checked={formData.hasBankDetails === v}
                      onChange={formReadOnly ? undefined : handleChange}
                      disabled={formReadOnly}
                    />
                    {v === "yes" ? "Yes" : "No"}
                  </label>
                ))}
              </div>
              {errors.hasBankDetails && (
                <span className="input-error-msg">{errors.hasBankDetails}</span>
              )}
            </div>
            {formData.hasBankDetails === "yes" && (
              <>
                <div className="form-grid">
                  {renderFields(
                    BANK_FIELDS,
                    formData,
                    errors,
                    formReadOnly ? undefined : handleChange,
                    formReadOnly ? undefined : handleBlur,
                    formReadOnly,
                  )}
                </div>
                {/* Bank Documents */}
                <div className="entity-docs-section">
                  <p className="entity-docs-note">Bank Documents</p>
                  <div className="entity-docs-grid">
                    {DOCS_BANK.map((doc) => {
                      const uploadable = docUploadable(doc.key);
                      const deficient = !!deficiencyMap[doc.key];
                      const existing = getExistingFile(doc.key);
                      const isUploading = isDocUploading
                        ? isDocUploading(doc.key)
                        : uploadingDocKeys instanceof Set
                          ? uploadingDocKeys.has(doc.key)
                          : false;
                      return (
                        <div
                          key={doc.key}
                          className={`bank-doc-card ${deficient ? "doc-deficient-wrap bank-doc-card--deficient" : ""}`}
                        >
                          <DocumentUpload
                            label={doc.label}
                            icon={
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#2563eb"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
                                <polyline points="12 13 12 9 10 11" />
                              </svg>
                            }
                            name={doc.key}
                            file={bankFiles[doc.key] || null}
                            existingFile={existing}
                            onViewExisting={
                              existing?.filePath
                                ? () =>
                                    handleViewDocument(
                                      existing.filePath,
                                      doc.label,
                                    )
                                : undefined
                            }
                            onChange={(e) => {
                              if (!uploadable || isUploading) return;
                              const file = e.target.files[0];
                              if (!file) return;
                              handleBankFileChange(doc.key, file);
                              handleDocumentUpload(doc.key, file);
                              setErrors((prev) => {
                                const err = { ...prev };
                                delete err[doc.key];
                                return err;
                              });
                            }}
                            required={doc.required}
                            error={errors[doc.key] || ""}
                            disabled={!uploadable || isUploading}
                            isUploading={isUploading}
                          />
                          {deficient && (
                            <span className="doc-deficient-label">
                              ⚠ Deficiency — please re-upload
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <ThirdPartySection
                  show={showThirdParty}
                  formData={formData}
                  errors={errors}
                  handleChange={formReadOnly ? undefined : handleChange}
                  handleBlur={formReadOnly ? undefined : handleBlur}
                  thirdPartyFile={thirdPartyFile}
                  handleThirdPartyFileChange={(file) => {
                    if (!docUploadable("third_party_file")) return;
                    setThirdPartyFile(file);
                    handleDocumentUpload("third_party_file", file);
                  }}
                  existingSignedFormFile={getExistingFile("third_party_file")}
                  onViewExisting={() => {
                    const f = getExistingFile("third_party_file");
                    if (f?.filePath)
                      handleViewDocument(
                        f.filePath,
                        "Signed Third Party Payment Indemnity Form",
                      );
                  }}
                  isDeficient={!!deficiencyMap["third_party_file"]}
                  isUploading={
                    isDocUploading ? isDocUploading("third_party_file") : false
                  }
                />
              </>
            )}
          </section>

          {/* ── Submit footer inside the card ── */}
          <div className="form-footer">
            {isUploadingDoc && (
              <span className="form-footer-upload-msg form-footer-upload-msg--uploading">
                <span className="footer-spinner" />
                Uploading document
                {uploadingDocCount > 1 ? `s (${uploadingDocCount})` : ""}...
                Please wait before submitting.
              </span>
            )}
            {!isUploadingDoc &&
              (hasPendingFileWithoutPath || hasFailedUploads) && (
                <span className="form-footer-upload-msg form-footer-upload-msg--error">
                  ⚠️ Document upload failed or pending file path. Please
                  re-upload to submit.
                </span>
              )}
            <button
              type="button"
              className="btn-save-draft"
              disabled={
                isSubmitting ||
                isUploadingDoc ||
                hasPendingFileWithoutPath ||
                hasFailedUploads
              }
              onClick={handleSaveDraft}
            >
              {isSubmitting
                ? "Saving…"
                : isUploadingDoc
                  ? "Uploading..."
                  : "Save Draft"}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={
                isSubmitting ||
                isUploadingDoc ||
                hasPendingFileWithoutPath ||
                hasFailedUploads
              }
            >
              {isSubmitting
                ? "Submitting..."
                : isUploadingDoc
                  ? `Uploading Document${uploadingDocCount > 1 ? `s (${uploadingDocCount})` : ""}...`
                  : hasPendingFileWithoutPath || hasFailedUploads
                    ? "Upload Pending / Failed"
                    : "Submit"}
            </button>
          </div>
        </div>
      </form>

      {/* Document Viewer Modal for previewing Aadhar, PAN, certificates, etc. */}
      {DocumentViewer && <DocumentViewer />}
    </div>
  );
};

export default UploadDetails;
